import { IReactionDisposer, makeAutoObservable, reaction } from 'mobx';
import * as subscriptions from '../../graphql/subscriptions';
import ErrorBoundaryStore from '../error-boundary-store/error-boundary-store';
import PollEntity, { IPollEntity } from './poll-entity';
import { TGraphQLSubscriptions } from '../../api/graphql-api-client/graphql-api-client';
import ErrorParsingService, { IErrorObject, IGraphQLErrorResponse } from '../../utils/services/error-parsing-service';
import ResponseEntity, { IResponseItem } from './response-entity';
import { INITIAL_VALUE, POLL_INCREMENT } from './../../constans/prioritize-constants';
import {
  TPollStatus,
  PollsTypes,
  PollsViewStatuses,
  TPollType,
  PollCommands,
  PollsTabsOnMobile,
  NUMBER_OF_RETRIES,
  DELAY,
  ITERATE_NUMBER,
  LIVE_ENDED_ERROR_MESSAGE_PART,
  RESET_SHARED_RESULTS_ERROR_MESSAGE,
  HIDE_SHARED_RESULTS_ON_LIVE_ENDED_ERROR_MESSAGE
} from '../../constans/polls-constants';
import { getErrorBoundaryStore } from '../error-boundary-store/error-boundary-store-instanse';
import { getPodiumInfoStore } from '../podium-info-store/podium-info-store-instance';
import PodiumInfoStore from '../podium-info-store/podium-info-store';
import { getKeyByValue } from '../../utils/helpers/polls-helper';
import PresenterSlideManagementStore from '../presenter-slide-management-store/presenter-slide-management-store';
import { getPresenterSlideManagementStore } from '../presenter-slide-management-store/presenter-slide-management-store-instance';
import LiveStreamStatusStore from '../live-stream-status-store/live-stream-status-store';
import { getLiveStreamStatusStore } from '../live-stream-status-store/live-stream-status-store-instance';
import { isLiveStreamEnded } from '../../constans/presenter-constansts';
import {
  createPollMutation,
  getPollsListQuery,
  ICommandResponse,
  ICreatePollResponse,
  IRequestPoll,
  ISubscriptionsData,
  pollCommandMutation,
  pollSubscription,
  updatePollAndChoicesMutation,
  getPollsChoicesQuery,
  deletePollMutation,
  updatePollMutation,
  generatePollResultsSlideApi,
  IPollSlideGenerateRequest,
  downloadPolls,
  IPollSlideGenerateResponse,
} from '../../api/dao/polls-dao';
import { getLivePresentationInfoApi, getPresentationInfoApi, getAssetInfo, getPresentationSlidesApi } from './../../api/dao/presenter-slide-dao';
import { getMixpanelService } from '../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../constans/mixpanel-contants';
import { COMMON_ERROR_CODES, RESOURCE_NOT_FOUND_CODE } from '../../constans/error-common-constants';

class PollsStore {
  public isReviewModalOpened: boolean;
  public isManagePollModalOpened: boolean;
  public pollsList: PollEntity[];
  public selectedPollId: string;
  public shouldShowNotificationModal: boolean;
  public isEllipsisMenuOpened: boolean;
  public isLoading: boolean;
  public shouldShowDeleteModal: boolean;
  public shouldShowResetModal: boolean;
  public shouldShowAddResultsSlideModal: boolean;
  public activeMobileTab: string;
  public isFullScreenModeEnabled: boolean;
  public errorParsingService: ErrorParsingService;
  public errorMessage: string;
  public isOnePollOpened: boolean;
  public isSomePollResultsShared: boolean;
  public livePresentationId: number;
  public isFullScreenModeSafari: boolean;
  public isPollsTabHidden: boolean;
  public isNextGenPlayer: boolean;
  // Share toggles this session issued, by poll id, until a server read confirms them.
  private pendingSharedResultsByPollId: Map<string, boolean>;
  // Whether the ended-live guard rejected the most recent pollCommand. Recorded per call because
  // `errorMessage` is never cleared on success, so reading it later could match a stale failure.
  private isLastCommandRejectedByEndedLive: boolean;
  private disposeLiveEndedWatcher: IReactionDisposer | undefined;

  constructor (
    private readonly podiumInfoStore: PodiumInfoStore = getPodiumInfoStore(),
    private readonly errorBoundary: ErrorBoundaryStore = getErrorBoundaryStore(),
    private readonly slideManagementStore: PresenterSlideManagementStore = getPresenterSlideManagementStore(),
    private readonly liveStreamStatusStore: LiveStreamStatusStore = getLiveStreamStatusStore()
  ) {
    this.initialize();
  }

  public initialize = (): void => {
    makeAutoObservable(this);
    this.pollsList = [];
    this.selectedPollId = '';
    this.shouldShowNotificationModal = false;
    this.isEllipsisMenuOpened = false;
    this.shouldShowDeleteModal = false;
    this.shouldShowResetModal = false;
    this.shouldShowAddResultsSlideModal = false;
    this.activeMobileTab = PollsTabsOnMobile.LIST;
    this.errorParsingService = new ErrorParsingService();
    this.isFullScreenModeSafari = false;
    this.isPollsTabHidden = false;
    this.isNextGenPlayer = false;
    this.isOnePollOpened = false;
    this.isSomePollResultsShared = false;
    this.pendingSharedResultsByPollId = new Map<string, boolean>();
    this.isLastCommandRejectedByEndedLive = false;
    this.watchLiveEnded();
  };

  // Ending the broadcast does not touch polls server-side — it only makes the API start rejecting every
  // poll command — so a poll still sharing its results keeps them on viewers' screens for good, and keeps
  // every other poll locked behind isResultsSharingLockedByOtherPoll. Hide them the moment the live ends,
  // the same way an open poll stops accepting votes, rather than waiting for a presenter to do it by hand.
  private watchLiveEnded = (): void => {
    // `initialize` is public and re-runnable, so replace any previous watcher instead of stacking a
    // second one — two reactions on the same transition would double every write the effect makes.
    this.disposeLiveEndedWatcher?.();
    this.disposeLiveEndedWatcher = reaction(
      () => isLiveStreamEnded(this.liveStreamStatusStore.liveStatus),
      (hasLiveEnded: boolean) => {
        if (hasLiveEnded) void this.hideSharedResultsOnLiveEnded();
      }
    );
  };

  // The app keeps a single PollsStore for its lifetime, so nothing calls this in production. It exists for
  // the throwaway instances tests build, whose reactions would otherwise keep firing on a shared store.
  public dispose = (): void => {
    this.disposeLiveEndedWatcher?.();
    this.disposeLiveEndedWatcher = undefined;
  };

  public setIsFullScreenModeSafari = (value: boolean): void => {
    this.isFullScreenModeSafari = value;
  };

  public getPollsList = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    try {
      const result = await getPollsListQuery(presentationId);

      // TODO Need fix graphql response type
      // @ts-ignore
      const { data: { listPolls: { items }}} = result;
      this.pollsList = items.map((item: IPollEntity) => new PollEntity(item));
      // A full load rebuilds every entity from the server, so nothing is awaiting confirmation anymore.
      this.pendingSharedResultsByPollId.clear();
      this.selectSharedPollIfAny();
      this.setIsOnePollOpened(this.checkIsSomePollOpened());
      this.setIsSomePollResultsShared(this.checkIsSomePollResultsShared());
      // The reaction in watchLiveEnded only sees the live *reaching* Ended; a session opened after the
      // broadcast already finished never observes that transition, so the loaded list is checked too.
      await this.hideSharedResultsOnLiveEnded();
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  // `resultsShared` comes back from listPolls, so any session — a refresh, a new tab, even a different
  // browser or machine — renders the correct Show/Hide Results state on load. Re-select whichever poll is
  // shared so the details panel (and its bg-white state) is shown immediately too.
  private selectSharedPollIfAny = (): void => {
    const sharedPoll = this.pollsList.find((poll: PollEntity) => poll.resultsShared);
    if (sharedPoll) {
      this.getPollChoisesList(sharedPoll.getId());
    }
  };

  public createPoll = async (poll: PollEntity): Promise<void> => {
    try {
      const serializedPoll = { ...this.serializePollRequestDTO(poll) };
      await createPollMutation(serializedPoll);
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public onCreatePollSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return pollSubscription(
      subscriptions.onCreatePoll,
      presentationId,
      this.onCreatePollCallback,
      () => this.onCreatePollSubscription());
  };

  public updatePoll = async (updateBody: Partial<IPollEntity>): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    try {
      await updatePollMutation(updateBody, presentationId);
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public onUpdatePollSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return pollSubscription(
      subscriptions.onUpdatePoll,
      presentationId,
      this.updatePollCallback,
      () => this.onUpdatePollSubscription());
  };

  // Resolves to `true` only when the command actually reached the API and succeeded. Errors are still
  // surfaced via `errorMessage` as before; the flag lets callers that applied optimistic state (see
  // sharePollResults) roll it back instead of leaving the UI describing something that never happened.
  public pollCommand = async (commandBody: Partial<IPollEntity>, command: PollCommands, isVod: boolean): Promise<boolean> => {
    this.isLastCommandRejectedByEndedLive = false;
    const presentationId = this.podiumInfoStore.getPresentationId();
    // Eligibility (live has a livePresentationId, VOD sends isVod) is decided by the caller (changePollStatus);
    // here we only guard against issuing the command without a presentation context.
    if (!presentationId) {
      return false;
    }

    try {
      this.isLoading = true;
      const result = await pollCommandMutation(commandBody, presentationId, this.livePresentationId, command, isVod);

      const parsedContent = JSON.parse(result.data?.pollCommand?.content as string);
      this.processPollCommand(parsedContent);

      return true;
    } catch (error) {
      const { errors: [extractedError] } = error as IGraphQLErrorResponse;
      this.errorMessage = this.errorParsingService.extractGraphQLError(extractedError);
      this.isLastCommandRejectedByEndedLive = this.errorMessage.includes(LIVE_ENDED_ERROR_MESSAGE_PART);

      // The ended-live event is left to the callers (see trackLiveEndedError) — sharePollResults recovers
      // from this same rejection for Hide Results, and counting a recovered flow as an error would keep
      // the metric flat for a fix the presenter sees working.
      if (this.errorMessage.includes('already open')) {
        getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.ERROR_ONE_POLL_OPENED);
      }

      return false;
    } finally {
      this.isLoading = false;
    }
  };

  // Only fires where the ended-live rejection actually survives to the presenter as a failure, so the
  // Hide Results path that recovers from it (sharePollResults) is not reported as an error.
  private trackLiveEndedError = (): void => {
    if (!this.isLastCommandRejectedByEndedLive) return;

    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.ERROR_ENDED_LIVE);
  };

  public getPollsTabConfig = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;
    try {
      const configs = await getPresentationSlidesApi(presentationId);
      // The Next-Gen Player wraps the payload under `presentation`; legacy exposes `project` at the root.
      // Read the flyout from whichever shape is present so the polls-tab flag resolves for both.
      const flyout = configs.presentation?.project?.flyout ?? configs.project?.flyout ?? configs.flyout;
      this.isPollsTabHidden = !!flyout?.tabs?.pollsTab?.hidden;
    } catch (error) {
      this.errorBoundary.setErrorMessage(error as IErrorObject);
    }
  };

  public getLiveInfoForCommand = async (): Promise<void> => {
    const liveInfo = this.slideManagementStore.returnLivePresentationInfo();
    this.livePresentationId = liveInfo?.id || 0;
    // Set the player type from the cached live record when present. Do NOT downgrade to Legacy when there
    // is no cached record — published presentations resolve asynchronously below, and downgrading here
    // would briefly flash the Legacy ("Add Results Slide") button before the published lookup completes.
    if (liveInfo) {
      this.isNextGenPlayer = !!liveInfo.use_new_player;
    }
    if (!this.livePresentationId) {
      const presentationId = this.podiumInfoStore.getPresentationId() || '';
      try {
        // Step 1: a live-presentations record (200) means the presentation is live; it is authoritative for the player type.
        const result = await getLivePresentationInfoApi(presentationId);
        this.livePresentationId = result.id;
        this.isNextGenPlayer = !!result.use_new_player;
      } catch (e) {
        // Only an exact 404 means the presentation is published (VOD, no live record) — then resolve the
        // player type from presentations/{id}. Any other error is left as-is (no published fallback).
        const errorCode = (e as IErrorObject)?.error_code?.toString();
        const isNotFound = errorCode === RESOURCE_NOT_FOUND_CODE || errorCode === COMMON_ERROR_CODES.NOT_FOUND;
        if (isNotFound) {
          try {
            const presentationInfo = await getPresentationInfoApi(presentationId);
            this.isNextGenPlayer = !!presentationInfo.use_new_player;
          // eslint-disable-next-line no-empty
          } catch (err) {
          }
        }
      }
    }
  };

  public getPollChoisesList = async (pollId: string | null): Promise<ResponseEntity[] | void> => {
    if (!pollId) return;
    this.selectedPollId = pollId;
    try {
      const result = await getPollsChoicesQuery(pollId);

      // TODO Need fix graphql response type
      // @ts-ignore
      const { data: { listPollChoices }} = result;
      const selectedPoll = this.getPollEntityById(pollId);
      selectedPoll.countVotes = listPollChoices.allVotesCount;
      selectedPoll.responses = listPollChoices.pollChoicesCountedList.map((el: IResponseItem) => new ResponseEntity(el));
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    } finally {
      this.isLoading = false;
    }
  };

  public deletePoll = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!this.selectedPollId || !presentationId) return;
    const deletedId = this.selectedPollId;
    try {
      await deletePollMutation(deletedId, presentationId);

      this.pollsList = this.pollsList.filter((el: IPollEntity) => el.pollId !== deletedId);
      this.selectedPollId = '';
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public onDeletePollSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return pollSubscription(
      subscriptions.onDeletePoll,
      presentationId,
      this.deletePollCallback,
      () => this.onDeletePollSubscription());
  };

  public onSavePollVotesSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return pollSubscription(
      subscriptions.onSavePollVotes,
      presentationId,
      this.savePollVotesCallback,
      () => this.onSavePollVotesSubscription());
  };

  public changePollStatus = async (poll: PollEntity, customStatus?: PollsViewStatuses): Promise<void> => {
    // A live presentation has a livePresentationId; a published one (VOD) does not.
    const isVod = !this.livePresentationId;
    const status = customStatus || poll.getNextPollStatus();
    const statusForUpdate = getKeyByValue(PollsViewStatuses, status) as TPollStatus;
    const command = poll.getCommand(PollsViewStatuses[statusForUpdate]);

    // The polls-tab enable/disable and published/VOD-poll support are Next-Gen Player features.
    if (this.isNextGenPlayer) {
      // Next-Gen (live or published): a hidden polls tab has no viewer-facing poll to open, so Open Poll
      // is ignored. Closing must still go through — the tab can be hidden while a poll is already open,
      // and swallowing STOP_POLL as well would leave that poll open to viewers with no way to close it.
      if (this.isPollsTabHidden && command === PollCommands.START_POLL) return;
    } else if (isVod) {
      // Legacy Player: no published/VOD poll pipeline — a published legacy presentation is a no-op.
      // Legacy live continues to open polls through the command pipeline below.
      return;
    }

    const updatePollBody = {
      status: statusForUpdate || getKeyByValue(PollsViewStatuses, PollsViewStatuses.NOT_STARTED),
      pollId: poll.getId(),
      value: poll.value,
      type: getKeyByValue(PollsTypes, poll.type) as TPollType
    };

    // Live and VOD (polls tab enabled) drive polls through the command pipeline;
    // the backend distinguishes VOD via the top-level isVod argument since it has no livePresentationId.
    const isCommandSuccessful = await this.pollCommand(updatePollBody, command, isVod);

    if (!isCommandSuccessful) this.trackLiveEndedError();
  };

  // Next-Gen Player only: toggle whether the poll results are shared with viewers. Sends a PollCommand
  // carrying `resultsShared` in the poll object, keeping the poll's current status (command matches it).
  public sharePollResults = async (poll: PollEntity, resultsShared: boolean): Promise<void> => {
    const statusForUpdate = getKeyByValue(PollsViewStatuses, poll.status) as TPollStatus;
    const command = poll.getCommand(poll.status);
    const isVod = !this.livePresentationId;
    const pollBody = {
      status: statusForUpdate,
      pollId: poll.getId(),
      value: poll.value,
      type: getKeyByValue(PollsTypes, poll.type) as TPollType,
      resultsShared
    };

    // Flip optimistically so the button and the cross-poll locks respond immediately...
    poll.setResultsShared(resultsShared);
    this.setIsSomePollResultsShared(this.checkIsSomePollResultsShared());
    this.pendingSharedResultsByPollId.set(poll.getId(), resultsShared);

    const isCommandSuccessful = await this.pollCommand(pollBody, command, isVod);
    const isHiddenAfterLiveEnded = !isCommandSuccessful
      && !resultsShared
      && this.isLastCommandRejectedByEndedLive
      && await this.persistResultsShared(poll, false);

    // Once the live presentation reaches Ended the backend rejects every poll command, Hide Results
    // included. Unlike Open Poll / Show Results it starts no new interactivity — it only clears results
    // viewers are already seeing — so persist it through updatePoll (the same mutation the backend uses to
    // store the flag) rather than rolling back and leaving the poll stuck on "Hide Results" forever.
    if (isHiddenAfterLiveEnded) {
      this.clearErrorMessage();
      return;
    }

    // ...but roll back if the command never landed, otherwise the button would read "Hide Results"
    // while nothing is shared with viewers, and the other polls would stay locked for no reason.
    if (!isCommandSuccessful) {
      this.trackLiveEndedError();
      poll.setResultsShared(!resultsShared);
      this.setIsSomePollResultsShared(this.checkIsSomePollResultsShared());
      this.pendingSharedResultsByPollId.delete(poll.getId());
    }
  };

  // Writes `resultsShared` straight to the poll record, bypassing the live-command pipeline. Reports
  // whether it landed so the caller can still roll back when even this fallback fails.
  private persistResultsShared = async (poll: PollEntity, resultsShared: boolean): Promise<boolean> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return false;

    try {
      await updatePollMutation({ pollId: poll.getId(), resultsShared }, presentationId);

      return true;
    } catch (error) {
      return false;
    }
  };

  // Clears `resultsShared` on every poll still sharing once the live has ended. Safe to call at any time:
  // it is a no-op while the broadcast is running, and while it has ended nothing else can set the flag
  // again (Show Results goes through the command pipeline, which the API rejects from then on).
  public hideSharedResultsOnLiveEnded = async (): Promise<void> => {
    if (!isLiveStreamEnded(this.liveStreamStatusStore.liveStatus)) return;

    const sharedPolls = this.pollsList.filter((poll: PollEntity) => poll.resultsShared);
    if (!sharedPolls.length) return;

    const clearedFlags = await Promise.all(sharedPolls.map(async (poll: PollEntity) => {
      // A write that never landed leaves the flag set on the record, so leave it set here too — showing
      // "Show Results" over results viewers can still see is the one state worse than not clearing it.
      if (!await this.persistResultsShared(poll, false)) return false;

      poll.setResultsShared(false);
      // listPolls is eventually consistent; mark the write pending so a stale re-read cannot revive it.
      this.pendingSharedResultsByPollId.set(poll.getId(), false);

      return true;
    }));

    this.setIsSomePollResultsShared(this.checkIsSomePollResultsShared());

    // Nothing retries a failed write on its own — the live is over, so no further status change will fire
    // the reaction again, and no button can be offered either since the API rejects every poll command
    // from here on. Say so, or the presenter never learns viewers are still looking at the results.
    if (clearedFlags.includes(false)) {
      this.errorMessage = HIDE_SHARED_RESULTS_ON_LIVE_ENDED_ERROR_MESSAGE;
    }
  };

  // Reset wipes the votes and returns the poll to Not Started, where shared results are meaningless — and
  // the Show/Hide Results toggle stops rendering (it needs a Completed poll with votes). So this is the
  // last chance to clear `resultsShared`: left set, it keeps every OTHER poll's Open Poll button and
  // ellipsis locked as "another poll is sharing results", with no control left anywhere to release them.
  public resetPoll = async (poll: PollEntity): Promise<void> => {
    if (poll.resultsShared) {
      // Abandon the reset when the flag cannot be cleared. Resetting anyway would strand it exactly as
      // described above, and silently — so leave the poll Completed, where the toggle still renders and
      // the presenter can retry, and tell them why nothing happened.
      if (!await this.persistResultsShared(poll, false)) {
        this.errorMessage = RESET_SHARED_RESULTS_ERROR_MESSAGE;

        return;
      }

      // Persisted before flipping locally, so a failed write can't desync the UI from the server.
      poll.setResultsShared(false);
      this.setIsSomePollResultsShared(this.checkIsSomePollResultsShared());
      // listPolls is eventually consistent; mark the write pending so a stale re-read cannot revive it.
      this.pendingSharedResultsByPollId.set(poll.getId(), false);
    }

    poll.setStatus(PollsViewStatuses.RESETED);
    poll.setResultSlideAdded(false);

    await this.updatePollAndPollChoices(poll);
  };

  public updatePollAndPollChoices = async (poll: PollEntity): Promise<void> => {
    try {
      const presentationId = this.podiumInfoStore.getPresentationId();
      const serializedPoll = { ...this.serializePollRequestDTO(poll) };
      await updatePollAndChoicesMutation(presentationId, serializedPoll.poll, serializedPoll.pollChoices);
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  private getAssetInfoWithRetry = async (param: number, numberOfRetries = NUMBER_OF_RETRIES, delay = DELAY): Promise<IPollSlideGenerateResponse> => {
    const response = await getAssetInfo(param);
    const condition = response.conversion_state === 'conversion_complete' && response.processing_state === 'processing_complete';
    if (condition) {
      return response;
    } else {
      if (numberOfRetries !== 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
        return await this.getAssetInfoWithRetry(param, numberOfRetries - ITERATE_NUMBER);
      } else {
        throw Error();
      }
    }
  };

  public generatePollResultsSlide = async (pollSlideGenerateBody: IPollSlideGenerateRequest, pollBody: Partial<IPollEntity>): Promise<void> => {
    try {
      const response = await generatePollResultsSlideApi(pollSlideGenerateBody);
      const assetResponse: IPollSlideGenerateResponse = await this.getAssetInfoWithRetry(response?.id);
      const relatedAssets = assetResponse?.related_assets[0];
      const smallThumbnail = relatedAssets ? relatedAssets.metadata.thumbnail_url : assetResponse?.thumbnail_url;
      const options = {
        smallThumbnail,
        largeThumbnail: assetResponse?.thumbnail_url,
        assetId: response?.id
      };

      await this.slideManagementStore.publishSegments(options);
      await this.updatePoll(pollBody);

      getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.ADD_RESULTS_SLIDE);
    } catch (e) {
      this.errorMessage = this.errorParsingService.parseApiErrorMessage(e as IErrorObject);
    }
  };

  public hideNotificationModal = (): void => {
    this.shouldShowNotificationModal = false;
  };

  public setShouldShowEllipsisMenu = (value: boolean): void => {
    this.isEllipsisMenuOpened = value;
  };

  public setShouldShowDeleteModal = (value: boolean): void => {
    this.shouldShowDeleteModal = value;
  };

  public setShouldShowAddResultsSlideModal = (value: boolean): void => {
    this.shouldShowAddResultsSlideModal = value;
  };

  public toggleManagePollModal = (): void => {
    this.isManagePollModalOpened = !this.isManagePollModalOpened;
  };

  public setShouldShowFullScreen = (value: boolean): void => {
    this.isFullScreenModeEnabled = value;
  };

  public setShouldShowResetModal = (value: boolean): void => {
    this.shouldShowResetModal = value;
  };

  public setActiveTab = (value: string): void => {
    this.activeMobileTab = value;
  };

  public setSelectedPollId = (value: string): void => {
    this.selectedPollId = value;
  };

  public setIsOnePollOpened = (value: boolean): void => {
    this.isOnePollOpened = value;
  };

  public setIsSomePollResultsShared = (value: boolean): void => {
    this.isSomePollResultsShared = value;
  };

  public clearErrorMessage = (): void => {
    this.errorMessage = '';
  };

  public checkIsSomePollOpened = (): boolean => {
    return this.pollsList.some((poll: PollEntity) => poll.status === PollsViewStatuses.IN_PROGRESS);
  };

  // Next-Gen Player only: while one poll's results are shared with viewers, other polls' Show Results
  // and Open Poll actions are locked to keep a single poll's results/voting live at a time.
  public checkIsSomePollResultsShared = (): boolean => {
    return this.pollsList.some((poll: PollEntity) => poll.resultsShared);
  };

  public getPollEntityById = (id?: string): PollEntity => {
    const index = this.pollsList.findIndex((el: PollEntity) => el.getId() === id);
    return this.pollsList[index];
  };

  private onCreatePollCallback = (data: Pick<ISubscriptionsData, 'onCreatePoll'>): void => {
    const serializedData = this.serializePollResponse(data.onCreatePoll);
    this.updateListOnCreate(serializedData);
  };

  private updatePollCallback = (data: Pick<ISubscriptionsData, 'onUpdatePoll'>): void => {
    const serializedData = data.onUpdatePoll;
    this.updatePollOnSubscription(serializedData);
  };

  private updatePollOnSubscription = (data: IPollEntity): void => {
    const pollEntity = this.getPollEntityById(data.pollId);
    data.responses = [...pollEntity.responses];
    data.countVotes = pollEntity.responses.length
      ? this.checkTotalCountsBasedOnResponsesCount(pollEntity)
      : data.countVotes;

    pollEntity.deserialize(data);
    this.setIsOnePollOpened(this.checkIsSomePollOpened());
    this.syncResultsSharedFromServer();
  };

  // A poll change reaches the other sessions as an onUpdatePoll broadcast, and that payload carries
  // neither `resultsShared` nor `countVotes` (the backend's update mutation doesn't return them, so
  // AppSync delivers null). Without this, a second tab / other browser / incognito window keeps showing
  // "Show Results" while results are already live to viewers. Re-read both from listPolls — the same
  // source a page load trusts — and merge them into the existing entities, never replacing the list, so
  // the loaded choices of whichever poll this session has open survive.
  private syncResultsSharedFromServer = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    try {
      const result = await getPollsListQuery(presentationId);

      // TODO Need fix graphql response type
      // @ts-ignore
      const { data: { listPolls: { items }}} = result;
      const hasPollStartedSharing = items
        .map((item: IPollEntity) => this.mergeServerPollState(item))
        .some(Boolean);

      this.setIsSomePollResultsShared(this.checkIsSomePollResultsShared());
      // Results going live in another window opens the details panel here too (the wrapper's bg-white
      // state) and loads the choices behind the result bars — exactly what a page load already does.
      if (hasPollStartedSharing) {
        this.selectSharedPollIfAny();
      }
      // A background re-read is best effort: a failure leaves the last known state in place and the next
      // poll update tries again, so it must not raise an error modal over the presenter's screen.
      // eslint-disable-next-line no-empty
    } catch (error) {
    }
  };

  // Returns whether this poll has just started sharing its results as far as this session is concerned.
  private mergeServerPollState = (item: IPollEntity): boolean => {
    const pollEntity = this.getPollEntityById(item.pollId);
    if (!pollEntity) return false;

    // Only heal the count for a poll whose choices this session never loaded. Where they are loaded the
    // live vote stream (onSavePollVotes) is ahead of the stored count, and must not be walked back.
    if (!pollEntity.responses.length && typeof item.countVotes === 'number') {
      pollEntity.countVotes = item.countVotes;
    }

    const commandedState = this.pendingSharedResultsByPollId.get(pollEntity.getId());
    const serverState = !!item.resultsShared;

    if (commandedState !== undefined) {
      // Our own toggle is awaiting confirmation. listPolls is an eventually consistent read, so a response
      // that contradicts what this session just commanded is stale: keep the optimistic value (viewers
      // already got the command) until a re-read agrees with it. Either way this session acted on the poll
      // itself, so its details panel is already open on it.
      if (commandedState === serverState) {
        this.pendingSharedResultsByPollId.delete(pollEntity.getId());
      }

      return false;
    }

    const hasStartedSharing = serverState && !pollEntity.resultsShared;
    pollEntity.setResultsShared(serverState);

    return hasStartedSharing;
  };

  private deletePollCallback = (data: Pick<ISubscriptionsData, 'onDeletePoll'>): void => {
    const { onDeletePoll: { Polls: polls }} = data;
    const deletedId = polls[0].pollId;
    this.pollsList = this.pollsList.filter((el: PollEntity) => el.getId() !== deletedId);
  };

  private savePollVotesCallback = (data: Pick<ISubscriptionsData, 'onSavePollVotes'>): void => {
    const { onSavePollVotes: voteResults } = data;
    const openedPoll = this.getPollEntityById(voteResults.pollId);
    openedPoll.countVotes = voteResults.allVotesCount;
    openedPoll.snapshot.countVotes = voteResults.allVotesCount;
    openedPoll.setResponseVoteResults(voteResults.pollVotesCount);
  };

  private processPollCommand = (data: ICommandResponse) => {
    const pollEntity = this.getPollEntityById(data.poll_id);
    pollEntity.publicAdjustment = data.public_adjustment;
    pollEntity.pollStatusTimestamp = data.poll_status_timestamp;
    pollEntity.status = PollsViewStatuses[data.status as TPollStatus];
    pollEntity.snapshot.poll_completed_timestamp = data.poll_status_timestamp;
  };

  private updateListOnCreate = (fromDTO: IPollEntity): void => {
    const isEditMode = this.pollsList.find(el => el.getId() === fromDTO.pollId);
    fromDTO.responses = fromDTO.responses?.map(el => new ResponseEntity(el));

    if (!isEditMode) {
      const newPoll = new PollEntity(fromDTO);
      this.pollsList.push(newPoll);
      this.selectedPollId = fromDTO.pollId || '';
    } else {
      const editedPoll = this.getPollEntityById(fromDTO.pollId);
      editedPoll.deserialize(fromDTO);
    }
  };

  private getPollPrioritizeIndex = (poll: PollEntity, index?: number): number => {
    const minIndexInArray = this.pollsList.length && Math.min(...this.pollsList.map(el => el.prioritizeIndex));
    const lastPrioritizeIndex = minIndexInArray || INITIAL_VALUE;
    const STEP = lastPrioritizeIndex > INITIAL_VALUE ? INITIAL_VALUE : POLL_INCREMENT;

    return index || poll.prioritizeIndex || lastPrioritizeIndex - STEP;
  };

  private serializePollRequestDTO = (poll: PollEntity, index?: number): IRequestPoll => {
    const newOrder = this.getPollPrioritizeIndex(poll, index);
    const presentationId = this.podiumInfoStore.getPresentationId();

    return {
      pollChoices: poll.serialize().responses?.map((el: ResponseEntity) => el.serialize()) || [],
      poll: {
        pollId: poll.getId(),
        hideForPresenter: poll.hideForPresenter,
        order: newOrder,
        status: getKeyByValue(PollsViewStatuses, poll.status) as TPollStatus,
        type: getKeyByValue(PollsTypes, poll.type) as TPollType,
        value: poll.value,
        result_slide_added: poll.result_slide_added,
      },
      presentationId: presentationId ?? ''
    };
  };

  private serializePollResponse = (data: ICreatePollResponse): IPollEntity => {
    const pollEntityDTO = { ...data.Polls[0] };
    pollEntityDTO.status = PollsViewStatuses[pollEntityDTO.status as TPollStatus];
    pollEntityDTO.type = PollsTypes[pollEntityDTO.type as TPollType];

    return {
      ...pollEntityDTO,
      responses: [...data.PollChoices]
    };
  };

  public downloadPollsCSV = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    try {
      await downloadPolls(presentationId);
    } catch (error) {
      this.errorBoundary.setErrorMessage(error as IErrorObject);
    }
  };

  //TODO: Delete it when will be total counts for update subscription
  private checkTotalCountsBasedOnResponsesCount = (targetEntity: PollEntity) => {
    let count = 0;
    for (const response of targetEntity.responses) {
      count += response.count || 0;
    }
    return count;
  };
}

export default PollsStore;
